import React, { ChangeEvent, MouseEvent, useEffect, useState } from 'react';
import { VisibilityTrackerContainer } from '@noths/polaris-client-components';
import {
  CheckboxWithLabel,
  colors,
  getProductImageURL,
  IconCheckmark,
  LoadingSpinner,
  Modal,
  NotificationInfo,
  SolidButton,
  TextButton,
  TextField,
} from '@noths/polaris-client-ribbons-design-system';

import { Product } from 'src/types';
import { waitForElement } from 'src/utils';
import * as copy from '../constants/copy';
import { useCheckUserEmailMarketingPreference, useTextAreaFormField } from '../hooks';
import { ContactPartnerSubmitPayload } from '../modules/thunks';
import {
  FieldValidationErrorPayload,
  MARKETING_PREFERENCES_VISIBILITY_NAME,
} from '../tracking/events';
import { ContactFormServerErrors } from '../types';
import * as styles from '../styles/ContactPartnerModal.styles';

export interface ContactPartnerModalProps {
  isModalOpen: boolean;
  messageSentSuccessfully: boolean;
  product: Product;
  onModalClose: VoidFunction;
  onSubmit: (payload: ContactPartnerSubmitPayload) => void;
  onSubmitError: (payload: FieldValidationErrorPayload) => void;
  serverErrors: ContactFormServerErrors;
}

const focusOnFirstInvalidField = async () => {
  const element = await waitForElement('[data-has-error="true"]');

  (element?.querySelector('textarea, input') as HTMLInputElement | null)?.focus();
};

export const ContactPartnerModal = ({
  isModalOpen,
  messageSentSuccessfully,
  product: { images, title, partner },
  onModalClose,
  onSubmit,
  onSubmitError,
  serverErrors,
}: ContactPartnerModalProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isUserLoggedIn, setIsUserLoggedIn] = useState(false);
  const [isUserOptedInToEmailMarketing, setIsUserOptedInToEmailMarketing] = useState(false);
  const shouldShowEmailAndNameFields = !isUserLoggedIn;

  const [
    fullName,
    onFullNameChange,
    fullNameError,
    validateFullName,
    resetFullNameState,
  ] = useTextAreaFormField({
    required: true,
  });
  const [email, onEmailChange, emailError, validateEmail, resetEmailState] = useTextAreaFormField({
    required: true,
  });
  const [
    message,
    onMessageChange,
    messageError,
    validateMessage,
    resetMessageState,
  ] = useTextAreaFormField({
    required: true,
  });
  const [formIsValid, setFormIsValid] = useState(false);
  const [hasSubmittedOnce, setHasSubmittedOnce] = useState(false);

  useEffect(() => {
    const validators = [
      validateMessage(),
      ...(shouldShowEmailAndNameFields ? [validateEmail(), validateFullName()] : []),
    ];

    const isValid = validators.every((isValid) => isValid);

    setFormIsValid(isValid);
  }, [
    fullName,
    email,
    message,
    validateFullName,
    validateEmail,
    validateMessage,
    shouldShowEmailAndNameFields,
  ]);

  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const onMarketingOptInChange = (e: ChangeEvent<HTMLInputElement>) =>
    setMarketingOptIn(e.target.checked);

  const handleSubmit = (e: MouseEvent) => {
    e.preventDefault();

    setHasSubmittedOnce(true);

    if (formIsValid) {
      onSubmit({ fullName, email, message, marketingOptIn });
      resetFullNameState();
      resetEmailState();
      resetMessageState();
      setHasSubmittedOnce(false);
      setFormIsValid(false);
      return;
    }

    if (!isUserLoggedIn) {
      if (fullNameError) {
        onSubmitError(copy.NAME_FIELD_ERROR);
      }
      if (emailError) {
        onSubmitError(copy.EMAIL_FIELD_ERROR);
      }
    }

    if (messageError) {
      onSubmitError(copy.QUESTION_FIELD_ERROR);
    }

    focusOnFirstInvalidField();
  };

  useCheckUserEmailMarketingPreference(({ loggedIn, isOptedIn }) => {
    setIsUserLoggedIn(loggedIn);
    setIsUserOptedInToEmailMarketing(isOptedIn);
    setIsLoading(false);
  });

  return (
    <Modal
      onModalClose={onModalClose}
      isModalOpen={isModalOpen}
      contentLabel={`Ask a question to ${partner.name}`}
    >
      {({ Header, Body, Footer }) => {
        if (isLoading) {
          return <LoadingSpinner css={styles.loadingSpinner} />;
        }

        return (
          <>
            <Header onCloseButtonClick={onModalClose} layout="stacked">
              <h2 css={styles.heading}>
                {messageSentSuccessfully ? (
                  <>
                    <IconCheckmark
                      css={styles.checkmarkIcon}
                      color={colors.functionalGreenPrimary}
                    />
                    <span>{copy.MESSAGE_SENT}</span>
                  </>
                ) : (
                  <>
                    Ask a question to <span css={styles.partnerName}>{partner.name}</span>
                  </>
                )}
              </h2>
            </Header>
            {messageSentSuccessfully ? (
              <>
                <Body>
                  <div css={styles.successBodyWrapper}>
                    <p>
                      Your question has been successfully sent to {partner.name} and they’ll get
                      back to you shortly.
                      <br />
                      <br />
                      In the meantime, why not see what else {partner.name} have to offer in their
                      shop.
                    </p>
                    <NotificationInfo textSize="XS">{copy.EMAIL_SPAM_REMINDER}</NotificationInfo>
                  </div>
                </Body>
                <Footer>
                  <div css={styles.successFooterButtons}>
                    <TextButton href={partner.links?.[1]?.rel || ''} css={styles.visitShopLink}>
                      {copy.VISIT_SHOP}
                    </TextButton>
                    <SolidButton css={styles.submitButton} size="fullWidth" onClick={onModalClose}>
                      {copy.RETURN_TO_PRODUCT_CTA}
                    </SolidButton>
                  </div>
                </Footer>
              </>
            ) : (
              <>
                <Body>
                  <form css={styles.form}>
                    <div css={styles.productInfo}>
                      <img
                        css={styles.productInfoImage}
                        alt=""
                        src={getProductImageURL(images[0].href, 'mini')}
                      />
                      <p css={styles.productInfoTitle}>{title}</p>
                    </div>
                    <div css={styles.fields}>
                      {shouldShowEmailAndNameFields && (
                        <div css={styles.nameAndEmailWrapper}>
                          <TextField
                            css={styles.textField}
                            label={copy.NAME_FIELD_LABEL}
                            id="contact-partner-fullname"
                            value={fullName}
                            onChange={onFullNameChange}
                            error={
                              (hasSubmittedOnce && fullNameError && copy.NAME_FIELD_ERROR) ||
                              serverErrors?.name
                            }
                          />
                          <TextField
                            css={styles.textField}
                            label={copy.EMAIL_FIELD_LABEL}
                            id="contact-partner-email"
                            value={email}
                            onChange={onEmailChange}
                            error={
                              (hasSubmittedOnce && emailError && copy.EMAIL_FIELD_ERROR) ||
                              serverErrors?.email
                            }
                          />
                        </div>
                      )}
                      <TextField
                        css={styles.textField}
                        label={copy.QUESTION_FIELD_LABEL}
                        id="contact-partner-question"
                        value={message}
                        rows={4}
                        onChange={onMessageChange}
                        error={
                          (hasSubmittedOnce && messageError && copy.QUESTION_FIELD_ERROR) ||
                          serverErrors?.message
                        }
                      />
                      <NotificationInfo textSize="XS">{copy.SENSITIVE_INFO_TEXT}</NotificationInfo>
                      {!isUserOptedInToEmailMarketing && (
                        <VisibilityTrackerContainer
                          sectionName={MARKETING_PREFERENCES_VISIBILITY_NAME}
                        >
                          <CheckboxWithLabel
                            css={styles.checkbox}
                            label={copy.CHECKBOX_LABEL}
                            checked={marketingOptIn}
                            textSize="XS"
                            onChange={onMarketingOptInChange}
                          />
                        </VisibilityTrackerContainer>
                      )}
                    </div>
                  </form>
                </Body>
                <Footer>
                  <SolidButton css={styles.submitButton} size="fullWidth" onClick={handleSubmit}>
                    {copy.SUBMIT_CTA}
                  </SolidButton>
                </Footer>
              </>
            )}
          </>
        );
      }}
    </Modal>
  );
};
